import { matchPath } from "react-router";
import type { AdminPageId, AdminPagePolicy } from "./permissions";
import { ADMIN_PAGE_POLICIES } from "./permissions";

export type AdminScreenId =
  | "01" | "02" | "03" | "04" | "05" | "06" | "07" | "08" | "09" | "10" | "11"
  | "12" | "13" | "14" | "15" | "16" | "17" | "18" | "19" | "20" | "21" | "22"
  | "23" | "24" | "25" | "26" | "27" | "28" | "29" | "30" | "31" | "32"
  | "33" | "34" | "35" | "36" | "37" | "38" | "39" | "40" | "41" | "42";
export type AdminNavigationScreenId = Exclude<AdminScreenId, "23" | "24" | "25" | "26" | "27" | "28" | "29" | "30" | "31" | "32">;
export type AdminSurface = "login" | "setup" | "operations";

export interface AdminRouteDefinition {
  readonly id: AdminPageId;
  readonly screen: AdminScreenId;
  readonly path: string;
  readonly surface: AdminSurface;
  readonly title: string;
  readonly description: string;
  readonly policy: AdminPagePolicy;
  readonly navigationScreen: AdminNavigationScreenId;
  readonly entityParam?: "memberId" | "transactionId" | "transferId" | "refundId" | "claimId" | "inquiryId" | "notificationId" | "contentId" | "configurationId" | "runId" | "issueId" | "jobId";
}

function route(
  id: AdminPageId,
  path: string,
  surface: AdminSurface,
  title: string,
  description: string,
  entityParam?: AdminRouteDefinition["entityParam"],
  navigationScreen?: AdminNavigationScreenId,
): AdminRouteDefinition {
  const screen = id.slice(-2) as AdminScreenId;
  return {
    id,
    screen,
    path,
    surface,
    title,
    description,
    policy: ADMIN_PAGE_POLICIES[id],
    navigationScreen: navigationScreen ?? screen as AdminNavigationScreenId,
    entityParam,
  };
}

export const ADMIN_ROUTE_DEFINITIONS = [
  route("ADM-S01", "/login", "login", "관리자 로그인", "허용 네트워크, 비밀번호와 TOTP 확인 후 운영 화면에 접근합니다."),
  route("ADM-S02", "/setup/password", "setup", "비밀번호 변경", "최초 로그인 또는 보안 정책에 따라 비밀번호를 변경합니다."),
  route("ADM-S03", "/setup/totp", "setup", "TOTP 설정", "관리자 2차 인증을 등록하고 확인합니다."),
  route("ADM-S04", "/", "operations", "서비스 현황", "서비스 현황과 우선 처리 업무를 확인합니다."),
  route("ADM-S05", "/members", "operations", "회원 관리", "권한 범위 안에서 회원을 검색합니다."),
  route("ADM-S06", "/members/:memberId", "operations", "회원 상세", "회원 상태, 상담메모와 처리 이력을 확인합니다.", "memberId", "05"),
  route("ADM-S07", "/transactions/reviews", "operations", "거래 심사", "심사 대기열을 조회하고 업무를 선점합니다."),
  route("ADM-S08", "/transactions/:transactionId", "operations", "거래 상세", "거래 심사, 결제와 송금 상태를 확인합니다.", "transactionId", "07"),
  route("ADM-S09", "/transfers", "operations", "송금 관리", "송금 대기와 실패 건을 조회합니다."),
  route("ADM-S10", "/transfers/:transferId", "operations", "송금 상세", "송금 상태와 실행 이력을 확인합니다.", "transferId", "09"),
  route("ADM-S11", "/refunds", "operations", "환불 관리", "해지와 환불 요청을 처리합니다."),
  route("ADM-S12", "/benefits", "operations", "혜택지원 심사", "혜택 기안, 승인과 지급 상태를 관리합니다."),
  route("ADM-S13", "/inquiries", "operations", "1:1 문의 관리", "문의를 배정하고 답변합니다."),
  route("ADM-S14", "/notifications", "operations", "알림 운영", "알림 정책, 템플릿과 캠페인을 관리합니다."),
  route("ADM-S15", "/contents", "operations", "콘텐츠 운영", "공지, FAQ와 이용가이드를 관리합니다."),
  route("ADM-S16", "/partners", "operations", "제휴 관리", "제휴 채널, 배너와 복귀·Callback 설정을 관리합니다."),
  route("ADM-S17", "/integrations", "operations", "연동 설정", "Provider 설정과 Credential 교체 이력을 관리합니다."),
  route("ADM-S18", "/operations/reconciliations", "operations", "대사 관리", "대사 실행과 불일치 이슈를 확인합니다."),
  route("ADM-S19", "/operations/logs", "operations", "운영 로그", "감사로그와 외부 API 호출이력을 조회합니다."),
  route("ADM-S20", "/operations/exports", "operations", "정산 Export", "정산 파일 생성과 다운로드 상태를 확인합니다."),
  route("ADM-S21", "/settings/admins", "operations", "관리자 계정", "관리자 계정, 역할과 TOTP 상태를 관리합니다."),
  route("ADM-S22", "/settings/ip-allowlist", "operations", "IP 허용목록", "관리자 접근 허용 네트워크를 관리합니다."),
  route("ADM-S23", "/refunds/:refundId", "operations", "환불 상세", "해지와 환불 요청의 계산 근거와 처리 상태를 확인합니다.", "refundId", "11"),
  route("ADM-S24", "/benefits/:claimId", "operations", "혜택지원 상세", "혜택 신청, 증빙과 승인·지급 이력을 확인합니다.", "claimId", "12"),
  route("ADM-S25", "/inquiries/:inquiryId", "operations", "문의 상세", "문의 본문, 첨부와 답변 이력을 확인합니다.", "inquiryId", "13"),
  route("ADM-S26", "/notifications/:notificationId", "operations", "알림 상세", "알림 내용과 발송 시도 이력을 확인합니다.", "notificationId", "14"),
  route("ADM-S27", "/contents/:contentId", "operations", "콘텐츠 상세", "콘텐츠 내용과 게시 상태를 확인하고 편집합니다.", "contentId", "15"),
  route("ADM-S28", "/contents/new", "operations", "콘텐츠 작성", "새 공지, FAQ 또는 이용가이드를 작성합니다.", undefined, "15"),
  route("ADM-S29", "/integrations/:configurationId", "operations", "연동 설정 상세", "Provider 설정과 Credential 교체 이력을 확인합니다.", "configurationId", "17"),
  route("ADM-S30", "/operations/reconciliations/:runId", "operations", "대사 실행 상세", "대사 실행 집계와 이 실행에 속한 이슈를 확인합니다.", "runId", "18"),
  route("ADM-S31", "/operations/reconciliations/:runId/issues/:issueId", "operations", "대사 이슈 상세", "대사 이슈의 소속, 내부값과 Provider 값을 확인합니다.", "issueId", "18"),
  route("ADM-S32", "/operations/exports/:jobId", "operations", "정산 Export 상세", "정산 파일 생성 상태와 다운로드 가능 여부를 확인합니다.", "jobId", "20"),
  route("ADM-S33", "/transactions", "operations", "거래 관리", "심사부터 결제·송금·취소까지 거래의 전체 상태를 조회합니다."),
  route("ADM-S34", "/subscriptions", "operations", "월정액 관리", "월정액 청구 대상과 완료·대기·실패·신규 최초결제를 조회합니다."),
  route("ADM-S35", "/external/identity-verifications", "operations", "휴대폰 본인확인 내역", "본인인증 제공자 요청과 결과를 조회합니다."),
  route("ADM-S36", "/external/card-payments", "operations", "신용카드 결제 내역", "카드 승인과 취소 결과를 조회합니다."),
  route("ADM-S37", "/external/card-failures", "operations", "신용카드 실패 내역", "카드 결제 실패 코드와 원인을 조회합니다."),
  route("ADM-S38", "/external/transfers", "operations", "송금거래 내역", "외부 송금 요청과 은행 처리 결과를 조회합니다."),
  route("ADM-S39", "/external/mobile-payments", "operations", "휴대폰 소액결제 내역", "정기 빌링키와 신규가입 최초 휴대폰 결제를 조회합니다."),
  route("ADM-S41", "/payment-agency", "operations", "결제대행자료", "납부 대상별 신용카드 승인·취소 내역을 조회합니다."),
  route("ADM-S42", "/membership-status", "operations", "가입/해지 현황", "결제수단별 당일·전일 가입, 해지와 유지 인원을 조회합니다."),
  route("ADM-S40", "/revenue", "operations", "매출 관리", "일자·가입채널별 결제·취소·환불과 매출을 조회합니다."),
] as const satisfies readonly AdminRouteDefinition[];

export const ADMIN_ROUTES_BY_ID = Object.fromEntries(
  ADMIN_ROUTE_DEFINITIONS.map((definition) => [definition.id, definition]),
) as Readonly<Record<AdminPageId, AdminRouteDefinition>>;

export const ADMIN_ROUTES_BY_SCREEN = Object.fromEntries(
  ADMIN_ROUTE_DEFINITIONS.map((definition) => [definition.screen, definition]),
) as Readonly<Record<AdminScreenId, AdminRouteDefinition>>;

export const ADMIN_NAVIGATION_GROUPS = [
  { label: "현황", screens: ["04", "42"] },
  { label: "회원·거래", screens: ["05", "07", "33", "09", "11", "12", "34", "13"] },
  { label: "운영 관리", screens: ["14", "15", "16", "17"] },
  { label: "3rd party 거래 관리", screens: ["35", "36", "37", "38", "39"] },
  { label: "운영 기록", screens: ["40", "41", "19"] },
  { label: "시스템", screens: ["21", "22"] },
] as const satisfies readonly { readonly label: string; readonly screens: readonly AdminScreenId[] }[];

export function pathForAdminScreen(screen: AdminNavigationScreenId): string {
  const definition = ADMIN_ROUTES_BY_SCREEN[screen];
  if (definition.entityParam) {
    throw new Error(`${definition.id} requires ${definition.entityParam}.`);
  }
  return definition.path;
}

export function pathForAdminEntityScreen(
  screen: "06" | "08" | "10" | "23" | "24" | "25" | "26" | "27" | "29" | "30",
  id: number,
): string {
  if (!Number.isSafeInteger(id) || id <= 0) throw new Error("Admin entity IDs must be positive safe integers.");
  const definition = ADMIN_ROUTES_BY_SCREEN[screen];
  return definition.path.replace(`:${definition.entityParam}`, String(id));
}

export function pathForReconciliationIssue(runId: number, issueId: number): string {
  if (![runId, issueId].every((id) => Number.isSafeInteger(id) && id > 0)) {
    throw new Error("Reconciliation IDs must be positive safe integers.");
  }
  return `/operations/reconciliations/${runId}/issues/${issueId}`;
}

export function pathForExportJob(jobId: string): string {
  const normalized = jobId.trim();
  if (!normalized) throw new Error("Export job ID is required.");
  return `/operations/exports/${encodeURIComponent(normalized)}`;
}

export function parseAdminEntityId(value: string | undefined): number | null {
  if (!value || !/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

export function parseAdminListSearch(
  searchParams: URLSearchParams,
  defaults: { readonly page?: number; readonly size?: number } = {},
): { readonly page: number; readonly size: number } {
  const page = Number(searchParams.get("page"));
  const size = Number(searchParams.get("size"));
  return {
    page: Number.isSafeInteger(page) && page >= 0 ? page : (defaults.page ?? 0),
    size: Number.isSafeInteger(size) && size >= 1 && size <= 100 ? size : (defaults.size ?? 10),
  };
}

export function safeAdminReturnPath(rawValue: string | null | undefined): string | null {
  if (!rawValue || !rawValue.startsWith("/") || rawValue.startsWith("//")) return null;
  try {
    const base = "https://carepass.invalid";
    const target = new URL(rawValue, base);
    if (target.origin !== base || target.username || target.password) return null;
    const isOperationPath = ADMIN_ROUTE_DEFINITIONS.some((definition) => (
      definition.surface === "operations" && matchPath({ path: definition.path, end: true }, target.pathname)
    ));
    return isOperationPath ? `${target.pathname}${target.search}` : null;
  } catch {
    return null;
  }
}

export function adminLoginPathAfterSessionEnd(reason: string, currentPath: string): string {
  const returnTo = reason === "expired" || reason === "unauthorized"
    ? safeAdminReturnPath(currentPath)
    : null;
  return returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : "/login";
}
